import { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/templates/AuthLayout';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { useAuth } from '@/hooks/useAuth';
import { usePrefersAdminExperience } from '@/hooks/usePrefersAdminExperience';
import { getHomeRoute } from '@/utils/roleRouting';
import { authAPI } from '@/services/api/auth.api';
import {
  createAcceptInvitationSchema,
  type AcceptInvitationFormData,
} from '@/utils/validation/auth.schemas';
import { ROUTES } from '@/constants/routes';
import styles from './AcceptInvitationPage.module.scss';

type ValidationState =
  | { status: 'loading' }
  | { status: 'invalid' }
  | { status: 'valid'; email: string; companyName: string | null };

export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { acceptInvitation } = useAuth();
  const prefersAdmin = usePrefersAdminExperience();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [validation, setValidation] = useState<ValidationState>({ status: 'loading' });

  const schema = useMemo(() => createAcceptInvitationSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInvitationFormData>({ resolver: zodResolver(schema) });

  // Valida el magic link al montar.
  useEffect(() => {
    if (!token) {
      setValidation({ status: 'invalid' });
      return;
    }
    let active = true;
    authAPI
      .validateInvitation(token)
      .then((res) => {
        if (!active) return;
        if (res.valid && res.email) {
          setValidation({ status: 'valid', email: res.email, companyName: res.companyName ?? null });
        } else {
          setValidation({ status: 'invalid' });
        }
      })
      .catch(() => {
        if (active) setValidation({ status: 'invalid' });
      });
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (data: AcceptInvitationFormData) => {
    try {
      const created = await acceptInvitation({ token, name: data.name, password: data.password });
      navigate(getHomeRoute(created, prefersAdmin), { replace: true });
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : t('auth.accept.error'),
      });
    }
  };

  if (validation.status === 'loading') {
    return (
      <AuthLayout title={t('auth.accept.loadingTitle')} subtitle={t('auth.accept.loadingSubtitle')}>
        <div className={styles.acceptInvitation__center}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      </AuthLayout>
    );
  }

  if (validation.status === 'invalid') {
    return (
      <AuthLayout title={t('auth.accept.invalidTitle')} subtitle={t('auth.accept.invalidSubtitle')}>
        <div className={styles.acceptInvitation__center}>
          <Button asChild variant="primary" size="lg" fullWidth>
            <Link to={ROUTES.AUTH.LOGIN}>{t('auth.accept.backToLogin')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const title = validation.companyName
    ? t('auth.accept.title', { company: validation.companyName })
    : t('auth.accept.titleNoCompany');

  return (
    <AuthLayout title={title} subtitle={t('auth.accept.subtitle', { email: validation.email })}>
      <form className={styles.acceptInvitation__form} onSubmit={handleSubmit(onSubmit)}>
        {errors.root && (
          <div className={styles.acceptInvitation__error} role="alert">
            {errors.root.message}
          </div>
        )}

        <FormField
          label={t('auth.accept.nameLabel')}
          type="text"
          placeholder={t('auth.accept.namePlaceholder')}
          error={errors.name?.message}
          required
          autoComplete="name"
          {...register('name')}
        />

        <FormField
          label={t('auth.accept.passwordLabel')}
          type="password"
          placeholder={t('auth.accept.passwordPlaceholder')}
          error={errors.password?.message}
          helperText={t('auth.accept.passwordHelper')}
          required
          autoComplete="new-password"
          {...register('password')}
        />

        <FormField
          label={t('auth.accept.confirmPasswordLabel')}
          type="password"
          placeholder={t('auth.accept.confirmPasswordPlaceholder')}
          error={errors.confirmPassword?.message}
          required
          autoComplete="new-password"
          {...register('confirmPassword')}
        />

        <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isSubmitting}>
          {t('auth.accept.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default AcceptInvitationPage;
